/* eslint-disable @typescript-eslint/no-explicit-any */

import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { UserAppointmentMessageList } from "@/Components/MessageScheduler";
import useFeature from "@/hooks/useFeature";
import { useCalendarStore } from "@/pages/Dashboard/Calendar/store/useCalendarStore";
import { TextVariant } from "@/types/text-variants";
import { cn, getLocalizedName } from "@/utils";
import { Table, Text } from "@mantine/core";
import { useForm, UseFormReturnType } from "@mantine/form";
import { useClickOutside, useDebouncedCallback } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import dayjs from "dayjs";
import humanizeDuration from "humanize-duration";
import _ from "lodash";
import React, { useEffect, memo } from "react";
import { parsePhoneNumber } from "react-phone-number-input";
import { isValidPhoneNumber } from "react-phone-number-input";
import {
  emailValid,
  numberAndCountryCodeToFullPhoneNumber,
} from "../../../Utilities";
import usePreferredLanguageStore from "../../../stores/usePreferredLanguageStore";
import { apiGetCustomerSearch } from "../apiAppointment";
import { useAppointmentModalStore } from "../useAppointmentModalStore_";
import { NotificationTriggerSection } from "./NotificationTriggerSection";
import { MessageModal } from "./UserAppointmentMessages";
import { api } from "@/lib/api-client";
import { Button, Divider, Spinner, Switch, useDisclosure } from "@heroui/react";
import { i18n } from "@lingui/core";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import useUserPermissionsStore from "@/stores/useUserPermissionsStore";
import { LimeLocalePicker } from "@/Components/NextBase/LimeLocalePicker";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { LimeTextarea } from "@/Components/NextBase/LimeTextarea";
import { LimePhoneInput } from "@/Components/NextBase/LimePhoneInput";
import { CustomerSubscriptionsList } from "./CustomerSubscriptions/CustomerSubscriptionList";
import { AppointmentCustomerFiles } from "./AppointmentCustomerFiles";
import { LimeNumberInput } from "@/Components/NextBase/LimeNumberInput";
import { LimeSwitch } from "@/Components/NextBase/LimeSwitch";
import { LimeCollapse } from "@/Components/NextBase/LimeCollapse";
import { channelIcons } from "@/constants/channelIcons";
import { UserRoundPen, UserSearch } from "lucide-react";
import { CustomerModal } from "@/pages/Dashboard/TaxRegister/Invoices/POS/CustomerModal";
import { ManageCustomerModal } from "@/Components/NextBase/ManageCustomerModal";
import { TRIGGERS } from "@/pages/Dashboard/NotificationSettings/constants/Triggers";

export type FormValues = {
  customerId: number | null;
  name: string;
  lastName: string;
  email: string;
  countryCode: string;
  gsm: string;
  price: number;
  notes: string;
  comment: string;
  isRepeating: boolean;
  repeatType: "week" | "day" | "month";
  repeatInterval: number;
  repeatDuration: number;
  payOnlyFirstAppointment: boolean;
  sendNotifications: boolean;
  createPayment: boolean;
  createPaymentLocale: string;
  createPaymentChannel: "sms" | "email";
  createPaymentTimeoutMinutes: number;
  extraDetails: any[];
  transferFilesToNewCustomer: boolean;
  customerSubscriptionId: number | null;
  gsmPlaceholder: string;
  emailPlaceholder: string;
  emailVisibility: "full" | "hidden" | "masked";
  gsmVisibility: "full" | "hidden" | "masked";
  notificationSettings: { id: number; enabled: boolean }[];
};

const Form = ({
  disableUnEditableFields,
  customerData,
  handleSubmitAppointmentForm,
  isNewAppointment,
  selectedServices,
  notificationSchedule,
  notificationLog,
  dayObject,
  fetchAppointmentData,
}: {
  disableUnEditableFields: boolean;
  customerData: any;
  handleSubmitAppointmentForm: (values: any) => void;
  isNewAppointment: boolean;
  selectedServices: any[];
  notificationSchedule: any[];
  notificationLog: any[];
  dayObject: any;
  fetchAppointmentData: (appointmentId: number) => void;
}) => {
  const customerSearchRef = useClickOutside(() => setSearchedCustomers([]));
  const [canSubmitForm, setCanSubmitForm] = React.useState(true);
  const { isFeatureEnabled } = useFeature();

  const { locations, selectedLocationId, calendarData } = useCalendarStore(
    (state) => ({
      locations: state.locations,
      selectedLocationId: state.selectedLocationId,
      calendarData: state.calendarData,
    }),
  );

  const {
    data: notificationSettings,
    isFetching: isFetchingNotificationSettings,
  } = api.notification.useGetNotificationSettings();

  const { locale } = i18n;

  const { permissions } = useUserPermissionsStore();

  const {
    isOpen: isCustomerModalOpen,
    onOpen: onOpenCustomerModal,
    onClose: onCloseCustomerModal,
  } = useDisclosure();

  const {
    isOpen: isManageCustomerModalOpen,
    onOpen: onOpenManageCustomerModal,
    onClose: onCloseManageCustomerModal,
  } = useDisclosure();

  const {
    appointmentData,
    setCurrentAppointmentGsm,
    setCurrentAppointmentEmail,
    setCanDismissDrawer,
  } = useAppointmentModalStore((state) => ({
    appointmentData: state.appointmentData,
    setCurrentAppointmentGsm: state.setCurrentAppointmentGsm,
    setCurrentAppointmentEmail: state.setCurrentAppointmentEmail,
    setCanDismissDrawer: state.setCanDismissDrawer,
  }));

  const [lastSelectedInput, setLastSelectedInput] = React.useState<
    "name" | "lastName" | "email" | "gsm" | null
  >(null);

  const { clientPreferredLanguage, userPreferredLanguage } =
    usePreferredLanguageStore((state) => state.preferredLanguage);

  const defaultLanguage =
    userPreferredLanguage || clientPreferredLanguage || "sl";

  const doesAnyServiceRequirePayment = selectedServices.some(
    (service) => service.formPaymentRequired,
  );
  const form = useForm<FormValues>({
    initialValues: {
      customerId: customerData?.customerId ?? null,
      name: customerData?.name ?? "",
      lastName: customerData?.lastName ?? "",
      email: customerData?.email ?? "",
      countryCode: customerData?.countryCode ?? "",
      gsm:
        numberAndCountryCodeToFullPhoneNumber(
          customerData?.gsm,
          customerData?.countryCode,
        ) ?? "",
      price: customerData?.price ?? 0.0,
      // birthday: customerData?.birthday
      //   ? dayjs(customerData?.birthday).toDate()
      //   : null,
      emailVisibility: customerData?.emailVisibility ?? "full",
      gsmVisibility: customerData?.gsmVisibility ?? "full",
      emailPlaceholder: customerData?.email ?? "",
      gsmPlaceholder: customerData?.gsm ?? "",
      notes: customerData?.notes ?? "",
      comment: customerData?.comment ?? "",
      isRepeating: false,
      repeatType: "week",
      repeatInterval: 1,
      repeatDuration: 1,
      payOnlyFirstAppointment: false,
      sendNotifications: true,
      createPayment:
        doesAnyServiceRequirePayment &&
        calendarData.isPaymentDefaultCheckedInDashboard,
      createPaymentLocale: defaultLanguage,
      createPaymentChannel: "sms",
      createPaymentTimeoutMinutes: calendarData.defaultPaymentTimeoutMinutes,
      extraDetails: customerData?.extraDetails ?? [],
      transferFilesToNewCustomer: false,
      notificationSettings: [] as { id: number; enabled: boolean }[],
      customerSubscriptionId: customerData?.customerSubscriptionId,
    },

    validate: {
      gsm: (value) => {
        if (!value) return null;

        const parsedGsm = parsePhoneNumber(value);
        if (!parsedGsm) {
          return t`Telefonska številka ni veljavna`;
        }

        if (!parsedGsm.isValid()) {
          return t`Telefonska številka ni veljavna`;
        }

        return null;
      },
    },
  });

  const {
    data: calculatedPaymentPriceData,
    isPending: isPaymentPricePending,
    processedErrorMessage: paymentPriceErrorMessage,
  } = api.appointments.useGetCalculatePaymentPrice(
    {
      appointmentParts: selectedServices.map((service) => ({
        ...service,
        serviceId: Number(service.serviceId),
        priceCents: Math.round(service.price * 100),
        discountAmount: service.discountAmount
          ? service.discountAmount *
            (service.discountType == "amount" ? 100 : 1)
          : 0,
      })),
      repeatData: form.getValues().isRepeating
        ? {
            type: form.getValues().repeatType as "week" | "day" | "month",
            interval: form.getValues().repeatInterval || 1,
            count: form.getValues().repeatDuration || 1,
          }
        : undefined,
      paymentDetails: {
        onlyFirstAppointment: form.getValues().payOnlyFirstAppointment,
      },
      startTime: dayObject?.format("YYYY-MM-DDTHH:mm"),
      locationId: selectedLocationId,
    },
    form.getValues().createPayment,
  );

  const {
    data: notificationChannels,
    isPending: isFetchingNotificationChannels,
  } = api.notification.useGetNotificationChannels();

  const [searchedCustomers, setSearchedCustomers] = React.useState<any[]>([]);
  const [isSearchingCustomers, setIsSearchingCustomers] = React.useState(false);

  useEffect(() => {
    if (customerData == null) {
      form.setValues({
        customerId: null,
        name: "",
        lastName: "",
        email: "",
        countryCode: "",
        gsm: "",
        price: 0.0,
        // birthday: null,
        notes: "",
        comment: "",
        isRepeating: false,
        repeatType: "week",
        repeatInterval: 1,
        repeatDuration: 1,
        sendNotifications: true,
        createPayment:
          doesAnyServiceRequirePayment &&
          calendarData.isPaymentDefaultCheckedInDashboard,
        createPaymentTimeoutMinutes: calendarData.defaultPaymentTimeoutMinutes,
        createPaymentLocale:
          userPreferredLanguage || clientPreferredLanguage || "sl",
        createPaymentChannel: "sms",
        extraDetails: [],
        transferFilesToNewCustomer: false,
        customerSubscriptionId: null,
      });
      return;
    }

    if (customerData?.userAppointmentId) {
      form.setValues({
        customerId: customerData?.customerId,
        name: customerData?.name,
        lastName: customerData?.lastName,
        email:
          customerData?.emailVisibility === "full"
            ? customerData?.email
            : undefined,
        countryCode: customerData?.countryCode,
        gsm:
          customerData?.gsmVisibility === "full"
            ? customerData?.gsm
            : undefined,
        price: customerData?.price ?? 0.0,
        // birthday: customerData?.birthday
        //   ? dayjs(customerData?.birthday).toDate()
        //   : null,
        notes: customerData?.notes,
        comment: customerData?.comment,
        emailVisibility: customerData?.emailVisibility ?? "full",
        gsmVisibility: customerData?.gsmVisibility ?? "full",
        emailPlaceholder: customerData?.email,
        gsmPlaceholder: customerData?.gsm,
        isRepeating: false,
        repeatType: "week",
        repeatInterval: 1,
        repeatDuration: 1,
        extraDetails: customerData?.extraDetails ?? [],
        transferFilesToNewCustomer: false,
        customerSubscriptionId: customerData?.customerSubscriptionId,
      });
    }
  }, [customerData?.userAppointmentId]);

  useEffect(() => {
    if (notificationSettings && appointmentData) {
      const isNew =
        isNewAppointment || (appointmentData?.isPreScheduled && !customerData);

      const serviceIds = selectedServices.map((service) => service.serviceId);

      const mergedSettings = notificationSettings.settings
        .filter((s) => s.enabled)
        .filter((s) => {
          const isCorrectService =
            s.all_services ||
            s.notification_setting_service.some((ns) =>
              serviceIds.includes(ns.serviceId),
            );

          const scheduledTime = (() => {
            if (s.trigger === "cronBeforeAppointment") {
              return dayjs(appointmentData.startTime)
                .subtract(s.time_offset, "minutes")
                .format("YYYY-MM-DDTHH:mm");
            } else if (s.trigger === "cronAfterAppointment") {
              return dayjs(appointmentData.startTime)
                .add(appointmentData.duration, "minutes")
                .add(s.time_offset, "minutes")
                .format("YYYY-MM-DDTHH:mm");
            }

            return null;
          })();

          const isCorrectTime =
            scheduledTime === null
              ? true
              : dayjs(scheduledTime).isAfter(dayjs());

          if (isNew) {
            return isCorrectService && isCorrectTime;
          } else {
            if (
              !["cronBeforeAppointment", "cronAfterAppointment"].includes(
                s.trigger,
              )
            ) {
              return false;
            }

            return (
              isCorrectService &&
              (isCorrectTime ||
                notificationSchedule.some(
                  (ns) => ns.notificationSettingId === s.notificationSettingId,
                ))
            );
          }
        })
        .filter(
          (s) => s.trigger !== "onCancellation" && s.trigger !== "onReschedule",
        )
        .toSorted((a, b) => {
          // onConfirmation triggers should be first
          if (a.trigger === "onConfirmation") {
            return -1;
          }
          if (b.trigger === "onConfirmation") {
            return 1;
          }

          // cronBeforeAppointment triggers sorted by time_offset (descending)
          if (
            a.trigger === "cronBeforeAppointment" &&
            b.trigger === "cronBeforeAppointment"
          ) {
            return (b.time_offset || 0) - (a.time_offset || 0);
          }
          if (a.trigger === "cronBeforeAppointment") {
            return -1;
          }
          if (b.trigger === "cronBeforeAppointment") {
            return 1;
          }

          // cronAfterAppointment triggers sorted by time_offset (ascending)
          if (
            a.trigger === "cronAfterAppointment" &&
            b.trigger === "cronAfterAppointment"
          ) {
            return (a.time_offset || 0) - (b.time_offset || 0);
          }
          if (a.trigger === "cronAfterAppointment") {
            return -1;
          }
          if (b.trigger === "cronAfterAppointment") {
            return 1;
          }

          // Any other triggers come last
          return 0;
        })
        .map((setting) => {
          if (isNew) {
            return {
              id: setting.notificationSettingId,
              enabled:
                form
                  .getValues()
                  .notificationSettings.find(
                    (s) => s.id === setting.notificationSettingId,
                  )?.enabled ?? true,
            };
          }

          return {
            id: setting.notificationSettingId,
            enabled: !!notificationSchedule.find(
              (ns) =>
                ns.notificationSettingId === setting.notificationSettingId,
            ),
          };
        });

      form.setValues({
        notificationSettings: mergedSettings,
      });
    }
  }, [
    notificationSettings,
    selectedServices,
    appointmentData?.startTime,
    appointmentData?.duration,
    customerData,
  ]);

  useEffect(() => {
    setCurrentAppointmentGsm(form.values.gsm);
  }, [form.values.gsm]);

  useEffect(() => {
    setCurrentAppointmentEmail(form.values.email);
  }, [form.values.email]);

  useEffect(() => {
    if (form.values.customerId) return;

    if (
      !form.values.name &&
      !form.values.lastName &&
      !form.values.email &&
      !form.values.gsm
    )
      return setSearchedCustomers([]);

    handleSearchCustomers();
  }, [
    form.values.name,
    form.values.lastName,
    form.values.email,
    form.values.gsm,
    form.values.customerId,
    setSearchedCustomers,
  ]);

  const handleSearchCustomers = useDebouncedCallback(async () => {
    const parsedGsm = form.values.gsm ? parsePhoneNumber(form.values.gsm) : "";

    setIsSearchingCustomers(true);
    const response = await apiGetCustomerSearch(
      form.values.name,
      form.values.lastName,
      form.values.email,
      parsedGsm ? parsedGsm.nationalNumber : "",
    ).catch((e) => {});
    setIsSearchingCustomers(false);

    if (response == null || response.status !== 200) {
      setSearchedCustomers([]);
      return;
    }

    setSearchedCustomers(
      response.data.map((customer: any) => ({
        ...customer,
        value: customer.name,
      })),
    );
  }, 250);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSubmitForm) return;
    setCanSubmitForm(false);

    if (isNewAppointment && !form.values.name) {
      setCanDismissDrawer(false);
      modals.openConfirmModal({
        title: t`Želite ustvariti termin brez osebe?`,
        children: (
          <Text size="sm">
            <Trans>
              Generirana bo nova stranka z imenom &quot;Brez Imena&quot; in
              naključnim priimkom. Želite nadaljevati?
            </Trans>
          </Text>
        ),
        labels: { confirm: t`Potrdi`, cancel: t`Prekliči` },
        onConfirm: () => {
          setCanSubmitForm(true);

          if (!form.validate()) {
            return;
          }

          handleSubmitAppointmentForm(form.values);
          setCanDismissDrawer(true);
        },
        onCancel: () => {
          setCanSubmitForm(true);
          setCanDismissDrawer(true);
        },
        onClose: () => {
          setCanDismissDrawer(true);
        },
        withinPortal: true,
        withCloseButton: false,
      });
    } else {
      if (!form.validate()) {
        return;
      }

      setCanDismissDrawer(true);
      handleSubmitAppointmentForm(form.values);
    }
    setCanSubmitForm(true);
  };

  const isPreScheduled = appointmentData?.isPreScheduled;

  const willChangeCustomer =
    !isPreScheduled &&
    !isNewAppointment &&
    form.getValues().customerId != appointmentData?.customers[0]?.customerId;

  const areNotificationSettingsLoading =
    isFetchingNotificationSettings || isFetchingNotificationChannels;

  return (
    <div>
      <div>
        <form
          id={"appointment-form"}
          data-identifier={"appointment-form"}
          onSubmit={handleSubmit}
        >
          <div className="flex flex-col gap-2">
            {willChangeCustomer && <Divider className="my-2" />}
            {customerData != null && !isPreScheduled && (
              <div>
                <div className="flex justify-end gap-1">
                  <Button
                    variant="flat"
                    size="sm"
                    startContent={<UserRoundPen size={16} />}
                    onPress={onOpenManageCustomerModal}
                  >
                    <Trans>Uredi stranko</Trans>
                  </Button>
                  <Button
                    variant="flat"
                    size="sm"
                    startContent={<UserSearch size={16} />}
                    onPress={onOpenCustomerModal}
                  >
                    <Trans>Zamenjaj stranko</Trans>
                  </Button>
                </div>
                {willChangeCustomer && (
                  <div>
                    <p className="text-end text-xs text-danger-500">
                      <Trans>
                        Stranka bo zamenjana z novo stranko ob posodobitvi
                        termina
                      </Trans>
                    </p>
                    {isFeatureEnabled("CUSTOMER_FILES") ? (
                      <LimeSwitch
                        title={t`Prenesi datoteke termina na novo stranko`}
                        {...form.getInputProps("transferFilesToNewCustomer", {
                          type: "checkbox",
                        })}
                      />
                    ) : undefined}
                  </div>
                )}
              </div>
            )}
            {willChangeCustomer && <Divider className="my-2" />}
            <div className="relative flex flex-row justify-between gap-2">
              <LimeInput
                id={"name"}
                data-identifier={"name"}
                {...{
                  ...form.getInputProps("name"),
                  onChange: (e) => {
                    form.setFieldValue("name", e.currentTarget.value);
                    form.setFieldValue("customerId", null);

                    setLastSelectedInput("name");
                  },
                }}
                endContent={
                  isSearchingCustomers ? <Spinner size={"sm"} /> : null
                }
                label={t`Ime`}
                className="flex-1"
                size="sm"
                isDisabled={disableUnEditableFields}
                autoComplete="do-not-autofill"
              />
              <LimeInput
                id={"lastName"}
                label={t`Priimek`}
                data-identifier={"last-name"}
                {...{
                  ...form.getInputProps("lastName"),
                  onChange: (e) => {
                    form.setFieldValue("lastName", e.currentTarget.value);
                    form.setFieldValue("customerId", null);

                    setLastSelectedInput("lastName");
                  },
                }}
                className="flex-1"
                size="sm"
                isDisabled={disableUnEditableFields}
                autoComplete="do-not-autofill"
              />

              {(lastSelectedInput === "name" ||
                lastSelectedInput === "lastName") && (
                <SearchedCustomers
                  customerSearchRef={customerSearchRef}
                  form={form}
                  searchedCustomers={searchedCustomers}
                  setSearchedCustomers={setSearchedCustomers}
                />
              )}
            </div>
            <div className="relative flex justify-between">
              <LimeInput
                id={"email"}
                label={t`E-pošta`}
                type="email"
                placeholder={form.getValues().emailPlaceholder}
                data-identifier={"email"}
                {...form.getInputProps("email")}
                onChange={(e) => {
                  form.setFieldValue("email", e.currentTarget.value);
                  form.setFieldValue("customerId", null);

                  setLastSelectedInput("email");
                }}
                isDisabled={disableUnEditableFields}
                size="sm"
                autoComplete="do-not-autofill"
                error={
                  form.getValues().email && !emailValid(form.getValues().email)
                    ? t`E-pošta ni veljavna`
                    : undefined
                }
              />
              {lastSelectedInput === "email" && (
                <SearchedCustomers
                  customerSearchRef={customerSearchRef}
                  form={form}
                  searchedCustomers={searchedCustomers}
                  setSearchedCustomers={setSearchedCustomers}
                />
              )}
            </div>
            <div
              style={{
                position: "relative",
              }}
            >
              {!disableUnEditableFields && (
                <LimePhoneInput
                  data-identifier={"phone-input"}
                  placeholder={form.getValues().gsmPlaceholder}
                  label={t`Telefonska številka`}
                  {...form.getInputProps("gsm")}
                  onChange={(e) => {
                    form.setFieldValue("gsm", e);
                    form.setFieldValue("customerId", null);

                    setLastSelectedInput("gsm");
                  }}
                  variant="flat"
                  isDisabled={disableUnEditableFields}
                  autoComplete="do-not-autofill"
                  defaultCountry={
                    locations?.find(
                      (l: any) => l.locationId === selectedLocationId,
                    )?.country || "SI"
                  }
                  error={
                    form.getValues().gsm &&
                    !isValidPhoneNumber(form.getValues().gsm)
                      ? t`Telefonska številka ni veljavna`
                      : undefined
                  }
                />
              )}

              {disableUnEditableFields && (
                <a
                  href={`tel:${form.values.gsm}`}
                  className={cn({
                    disabled:
                      form.values.gsm && !isValidPhoneNumber(form.values.gsm),
                  })}
                >
                  <LimePhoneInput
                    placeholder={form.getValues().gsmPlaceholder}
                    value={form.values.gsm}
                    onChange={() => {}}
                    data-identifier={"phone-input"}
                    label={t`Telefonska številka`}
                    isDisabled={disableUnEditableFields}
                    autoComplete="do-not-autofill"
                    variant="flat"
                    defaultCountry={
                      locations?.find(
                        (l: any) => l.locationId === selectedLocationId,
                      )?.country || "SI"
                    }
                    disablePointerEvents
                  />
                </a>
              )}

              {lastSelectedInput === "gsm" && (
                <SearchedCustomers
                  customerSearchRef={customerSearchRef}
                  form={form}
                  searchedCustomers={searchedCustomers}
                  setSearchedCustomers={setSearchedCustomers}
                />
              )}
            </div>
            {/* <DateInput
              label={t`Datum rojstva`}
              {...form.getInputProps("birthday")}
              valueFormat="D.M.YYYY"
              disabled={disableUnEditableFields}
            ></DateInput> */}
            {form.values.notes && (
              <LimeTextarea
                label={t`Komentar na stranki`}
                {...form.getInputProps("notes")}
                isDisabled={disableUnEditableFields}
              />
            )}
            <LimeTextarea
              label={t`Komentar termina`}
              {...form.getInputProps("comment")}
              data-identifier={"appointment-comment"}
              minRows={4}
              isDisabled={!permissions.manage_appointments}
            />
            {form.values.extraDetails &&
              Object.entries(form.values.extraDetails).length > 0 && (
                <div className="flex flex-col">
                  <Text variant={TextVariant.BodyEmphasized}>
                    <Trans>Dodatne informacije</Trans>
                  </Text>
                  <Table
                    withRowBorders={false}
                    bg={"#f8f8f8"}
                    styles={{ table: { borderRadius: "4px" } }}
                  >
                    {form.values.extraDetails.map(
                      (extraDetail: any, index: number) => {
                        const inputElement = (() => {
                          switch (extraDetail.type) {
                            case "multi-select":
                            case "checkbox":
                              return (
                                <Text>
                                  {extraDetail.value
                                    .map(
                                      (v: any) =>
                                        v.defaultLanguage ||
                                        Object.values(v)[0],
                                    )
                                    .join(", ")}
                                </Text>
                              );
                            case "radio":
                            case "select":
                              return (
                                <Text>
                                  {extraDetail.value.defaultLanguage ||
                                    Object.values(extraDetail.value)[0]}
                                </Text>
                              );
                            case "date":
                              return (
                                <Text>
                                  {dayjs(extraDetail.value, "MM-DD-YYYYTHH:mm")
                                    .toDate()
                                    .toLocaleString(locale, {
                                      day: "2-digit",
                                      month: "numeric",
                                      year: "numeric",
                                    })}
                                </Text>
                              );
                            case "time":
                              return (
                                <Text>
                                  {dayjs(extraDetail.value, "MM-DD-YYYYTHH:mm")
                                    .toDate()
                                    .toLocaleString(locale, {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                </Text>
                              );
                            default:
                              return <Text>{extraDetail.value}</Text>;
                          }
                        })();

                        return (
                          <Table.Tr>
                            <Table.Td pt={2} pb={2}>
                              <Text variant={TextVariant.BodyEmphasized}>
                                {getLocalizedName(
                                  Object.entries<any>(extraDetail.label).map(
                                    ([lang, value]) => ({
                                      language: lang,
                                      name: value,
                                    }),
                                  ),
                                )}
                              </Text>
                            </Table.Td>
                            <Table.Td pt={0} pb={0}>
                              {inputElement}
                            </Table.Td>
                          </Table.Tr>
                        );
                      },
                    )}
                  </Table>
                </div>
              )}

            {isFeatureEnabled("SUBSCRIPTIONS") &&
            form.getValues().customerId != null ? (
              <CustomerSubscriptionsList
                customerId={form.getValues().customerId!}
                customerSubscriptionId={form.getValues().customerSubscriptionId}
                onCustomerSubscriptionChange={(customerSubscriptionId) => {
                  form.setFieldValue(
                    "customerSubscriptionId",
                    customerSubscriptionId,
                  );
                }}
                isPreScheduled={appointmentData?.isPreScheduled}
                isNewAppointment={isNewAppointment}
              />
            ) : null}

            {areNotificationSettingsLoading ? (
              <Spinner className="mt-4" />
            ) : null}

            {isNewAppointment ||
            (appointmentData?.isPreScheduled && !customerData) ? (
              form.getValues().notificationSettings.length ? (
                <div className="flex flex-col gap-1 border-b-1 border-gray-200 pb-4">
                  <div className="flex justify-between">
                    <span>
                      <Trans>Obvestila</Trans>
                    </span>
                    <Button
                      size="sm"
                      variant="flat"
                      color="primary"
                      onPress={() => {
                        const areAllEnabled =
                          form.values.notificationSettings.every(
                            (setting) => setting.enabled,
                          );

                        form.setValues({
                          notificationSettings:
                            form.values.notificationSettings.map((setting) => ({
                              ...setting,
                              enabled: !areAllEnabled,
                            })),
                        });
                      }}
                    >
                      {form.values.notificationSettings.every(
                        (setting) => setting.enabled,
                      ) ? (
                        <Trans>Izključi vse</Trans>
                      ) : (
                        <Trans>Vključi vse</Trans>
                      )}
                    </Button>
                  </div>

                  {form.values.notificationSettings.map((setting, index) => {
                    const settingDetails = notificationSettings?.settings.find(
                      (s) => s.notificationSettingId === setting.id,
                    );

                    const trigger = settingDetails?.trigger || "";

                    const notificationType =
                      settingDetails?.notificationType || "";

                    const notificationTypeLabel = (() => {
                      if (notificationType === "fallbackFlow") {
                        return notificationChannels?.channels.map(
                          (c) => channelIcons[c.notificationType],
                        );
                      }

                      return channelIcons[notificationType as "email"];
                    })();

                    const humanizedDuration = [
                      "cronBeforeAppointment",
                      "cronAfterAppointment",
                    ].includes(trigger)
                      ? `(${humanizeDuration(
                          (settingDetails?.time_offset || 0) * 60000,
                        )})`
                      : "";

                    const label = TRIGGERS().find(
                      (t) => t.trigger === trigger,
                    )?.label;

                    return (
                      <div key={setting.id}>
                        <LimeSwitch
                          size="sm"
                          {...form.getInputProps(
                            `notificationSettings.${index}.enabled`,
                            {
                              type: "checkbox",
                            },
                          )}
                          classNames={{ label: "w-full" }}
                          isCompact
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex flex-col gap-1">
                              <div>{label}</div>
                              <div>{humanizedDuration}</div>
                            </div>
                            <div className="flex items-center gap-2">
                              {notificationTypeLabel}
                            </div>
                          </div>
                        </LimeSwitch>
                      </div>
                    );
                  })}
                </div>
              ) : null
            ) : (
              <div className="flex flex-col">
                <span className="text-sm font-medium">
                  <Trans>Obvestila</Trans>
                </span>
                <div className="flex flex-col gap-1 pb-2">
                  {form.values.notificationSettings.map((setting, index) => {
                    const settingDetails = notificationSettings?.settings.find(
                      (s) => s.notificationSettingId === setting.id,
                    );

                    const trigger = settingDetails?.trigger || "";

                    const notificationType =
                      settingDetails?.notificationType || "";

                    const notificationTypeLabel = (() => {
                      if (notificationType === "fallbackFlow") {
                        return notificationChannels?.channels.map(
                          (c) => channelIcons[c.notificationType],
                        );
                      }

                      return channelIcons[notificationType as "email"];
                    })();

                    const scheduled = notificationSchedule.find(
                      (ns) => ns.notificationSettingId === setting.id,
                    );

                    const scheduledTime = scheduled?.scheduledTime;

                    const humanizedDuration = [
                      "cronBeforeAppointment",
                      "cronAfterAppointment",
                    ].includes(trigger)
                      ? `(${humanizeDuration(
                          (settingDetails?.time_offset || 0) * 60000,
                        )})`
                      : "";

                    const label = TRIGGERS().find(
                      (t) => t.trigger === trigger,
                    )?.label;

                    return (
                      <div key={setting.id}>
                        <LimeSwitch
                          size="sm"
                          {...form.getInputProps(
                            `notificationSettings.${index}.enabled`,
                            {
                              type: "checkbox",
                            },
                          )}
                          classNames={{ label: "w-full" }}
                          isCompact
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex flex-col gap-1">
                              <div>{label}</div>
                              <div>{humanizedDuration}</div>
                              <span className="text-xs text-gray-500">
                                {scheduledTime}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              {notificationTypeLabel}
                            </div>
                          </div>
                        </LimeSwitch>
                      </div>
                    );
                  })}
                </div>
                {notificationLog.length ? (
                  <>
                    <Text variant={TextVariant.BodyEmphasized}>
                      <Trans>Poslana</Trans>
                    </Text>

                    <div className="ml-5">
                      <NotificationTriggerSection
                        notificationLog={notificationLog}
                        triggers={["onConfirmation"]}
                        label={t`Ob potrditvi`}
                      ></NotificationTriggerSection>

                      <NotificationTriggerSection
                        notificationLog={notificationLog}
                        triggers={["onReschedule"]}
                        label={t`Ob spremembi termina`}
                      ></NotificationTriggerSection>

                      <NotificationTriggerSection
                        notificationLog={notificationLog}
                        triggers={["onCancelation"]}
                        label={t`Ob odpovedi`}
                      ></NotificationTriggerSection>

                      <NotificationTriggerSection
                        notificationLog={notificationLog}
                        triggers={[
                          "cronBeforeAppointment",
                          "cronAfterAppointment",
                        ]}
                        label={t`Pred/po terminu`}
                      ></NotificationTriggerSection>
                    </div>
                  </>
                ) : null}
                {isFeatureEnabled("MESSAGE_SCHEDULE") &&
                !isNewAppointment &&
                customerData?.userAppointmentId ? (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <Text variant={TextVariant.BodyEmphasized}>
                        <Trans>Sporočila</Trans>
                      </Text>
                      <MessageModal
                        userAppointmentId={customerData.userAppointmentId}
                      ></MessageModal>
                    </div>

                    <div className="flex flex-col">
                      <UserAppointmentMessageList
                        userAppointmentId={customerData.userAppointmentId}
                      ></UserAppointmentMessageList>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
            {isNewAppointment && isFeatureEnabled("PAYMENTS") && (
              <div
                className={cn("rounded-md transition-all", {
                  "bg-stone-100 p-2": form.values.createPayment,
                })}
              >
                <LimeSwitch
                  title={t`Zahtevaj plačilo in ga pošlji stranki`}
                  {...form.getInputProps("createPayment", {
                    type: "checkbox",
                  })}
                  description={t`Potrebna je tel. št. ali e-mail`}
                  isCompact
                />

                <LimeCollapse isOpen={form.values.createPayment}>
                  <div>
                    <div className="flex items-center gap-2 py-2">
                      {!isPaymentPricePending &&
                      calculatedPaymentPriceData?.success != false ? (
                        <p
                          className={cn("mt-3 transition-colors", {
                            "text-gray-500": isPaymentPricePending,
                          })}
                        >
                          <Trans>Za plačati</Trans>{" "}
                          {!paymentPriceErrorMessage &&
                            calculatedPaymentPriceData?.formattedPrice}
                        </p>
                      ) : (
                        <p>Napaka pri izračunu cene</p>
                      )}
                      {isPaymentPricePending && <Spinner size="sm" />}
                    </div>
                    <LimeAlert
                      className="flex-1"
                      message={paymentPriceErrorMessage}
                      color="danger"
                    />

                    <div className="mb-2">
                      <LimeNumberInput
                        label={t`Čas za plačilo (min)`}
                        minValue={1}
                        size="sm"
                        variant="bordered"
                        step={1}
                        {...form.getInputProps("createPaymentTimeoutMinutes")}
                      />
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <LimeLocalePicker
                        {...form.getInputProps("createPaymentLocale")}
                        className="flex-1"
                        variant="bordered"
                        size="sm"
                        label={undefined}
                      />

                      <LimeSelect
                        {...form.getInputProps("createPaymentChannel")}
                        items={[
                          {
                            key: "sms",
                            label: t`SMS`,
                          },
                          {
                            key: "email",
                            label: t`Email`,
                          },
                        ]}
                        disallowEmptySelection
                        className="flex-1"
                        variant="bordered"
                        size="sm"
                        selectionMode="single"
                      />
                    </div>
                  </div>
                </LimeCollapse>
              </div>
            )}
            {isNewAppointment && (
              <div
                className={cn("rounded-md transition-all", {
                  "bg-stone-100 p-2": form.values.isRepeating,
                })}
              >
                <LimeSwitch
                  data-identifier={"repeating-appointment-switch"}
                  title={t`Ponavljajoč termin`}
                  {...form.getInputProps("isRepeating", {
                    type: "checkbox",
                  })}
                  isCompact
                />
                {form.values.isRepeating && (
                  <div className="mt-3 flex flex-col gap-4">
                    <div className="flex items-center justify-between gap-3">
                      <Text style={{ whiteSpace: "pre" }}>
                        <Trans>Ponovi vsak</Trans>
                      </Text>
                      <LimeNumberInput
                        data-identifier={"repeat-interval"}
                        placeholder={"1"}
                        {...form.getInputProps("repeatInterval")}
                        minValue={1}
                        size="sm"
                        variant="bordered"
                        step={1}
                      />
                      <LimeSelect
                        data-identifier={"repeat-type-select"}
                        {...form.getInputProps("repeatType")}
                        variant="bordered"
                        items={[
                          { label: t`Dan`, key: "day" },
                          { label: t`Teden`, key: "week" },
                          { label: t`Mesec`, key: "month" },
                        ]}
                        size="lg"
                        defaultValue={"week"}
                      />
                    </div>
                    <LimeNumberInput
                      data-identifier={"repeat-duration"}
                      label={t`Število ponavljanj`}
                      placeholder={"1"}
                      {...form.getInputProps("repeatDuration")}
                      minValue={1}
                      size="sm"
                      variant="bordered"
                      step={1}
                    />
                    {form.values.createPayment && (
                      <LimeSwitch
                        title={t`Plačaj samo prvi termin`}
                        {...form.getInputProps("payOnlyFirstAppointment", {
                          type: "checkbox",
                        })}
                        isCompact
                      />
                    )}
                  </div>
                )}
              </div>
            )}
            {customerData?.customFields != null &&
              customerData?.customFields?.length > 0 && (
                <div className="rounded-lg bg-[#F7F7F7] px-2 py-1">
                  <Text fw={500} size={"sm"}>
                    <Trans>Polja po meri</Trans>
                  </Text>
                  <div className="flex flex-col gap-1">
                    {customerData?.customFields.map(
                      (field: any, index: number) => {
                        let value = field.value;
                        if (dayjs(field.value, "YYYY-MM-DD").isValid()) {
                          value = dayjs(field.value, "YYYY-MM-DD").format(
                            "DD.MM.YYYY",
                          );
                        }

                        return (
                          <div className="flex gap-1" key={field.field}>
                            <Text size={"sm"} fw={500}>
                              {field.field}:{" "}
                            </Text>
                            <Text size={"sm"}>{value}</Text>
                          </div>
                        );
                      },
                    )}
                  </div>
                </div>
              )}
          </div>
          <button
            id={"appointment-form-submit"}
            type={"submit"}
            className="hidden"
          ></button>
        </form>

        {isFeatureEnabled("CUSTOMER_FILES") &&
          customerData?.userAppointmentId && (
            <AppointmentCustomerFiles
              customerId={customerData?.customerId}
              userAppointmentId={customerData.userAppointmentId}
            />
          )}
      </div>

      <CustomerModal
        isOpen={isCustomerModalOpen}
        preSelectedCustomerId={form.getValues().customerId ?? undefined}
        types={["physical"]}
        confirmButtonProps={{
          text: t`Zamenjaj stranko`,
        }}
        handleClose={(data?: { customerId?: number; companyId?: number }) => {
          onCloseCustomerModal();

          if (data?.customerId != null) {
            form.setFieldValue("customerId", data.customerId);
          }
        }}
      />

      <ManageCustomerModal
        isOpen={isManageCustomerModalOpen}
        handleClose={({ customerId, companyId }) => {
          onCloseManageCustomerModal();

          if (customerId != null || companyId != null) {
            fetchAppointmentData(appointmentData?.appointmentId);
          }
        }}
        editingEntityId={form.getValues().customerId ?? undefined}
        defaultEntityType="physical"
      />
    </div>
  );
};

const SearchedCustomers = memo(function SearchedCustomers({
  searchedCustomers,
  setSearchedCustomers,
  form,
  customerSearchRef,
}: {
  searchedCustomers: any[];
  setSearchedCustomers: (customers: any[]) => void;
  form: UseFormReturnType<any>;
  customerSearchRef: React.RefObject<HTMLDivElement>;
}) {
  return (
    <div
      data-identifier="searched-customers"
      className={cn(
        "absolute left-0 right-0 top-full z-[1000] max-h-64 overflow-y-auto rounded-xl border-1 border-gray-200 bg-white",
        {
          hidden: !searchedCustomers.length,
        },
      )}
      ref={customerSearchRef}
    >
      {searchedCustomers.map((customer) => (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setSearchedCustomers([]);
              form.setFieldValue("customerId", customer.customerId);
            }
          }}
          className="rounded-tr-xl p-2 pb-0 first:rounded-tl-xl last:rounded-bl-xl last:rounded-br-xl hover:cursor-pointer hover:bg-[#f5f5f5]"
          key={customer.customerId}
          onClick={() => {
            setSearchedCustomers([]);
            form.setFieldValue("customerId", customer.customerId);
            form.setFieldValue("name", customer.name || "");
            form.setFieldValue("lastName", customer.lastName || "");

            form.setFieldValue("emailVisibility", customer.emailVisibility);
            form.setFieldValue("gsmVisibility", customer.gsmVisibility);

            form.setFieldValue("emailPlaceholder", customer.email);
            form.setFieldValue("gsmPlaceholder", customer.gsm);

            if (customer.emailVisibility === "full") {
              form.setFieldValue("email", customer.email || "");
            }

            if (customer.gsmVisibility === "full") {
              form.setFieldValue(
                "gsm",
                numberAndCountryCodeToFullPhoneNumber(
                  customer.gsm,
                  customer.countryCode,
                ),
              );
            }

            form.setFieldValue("countryCode", customer.countryCode);
            // form.setFieldValue(
            //   "birthday",
            //   customer.birthday ? dayjs(customer.birthday).toDate() : null
            // );
            form.setFieldValue("notes", customer.notes);
            form.setFieldValue("customerId", customer.customerId);
          }}
        >
          <div className="flex items-start gap-1 text-sm">
            <div className="flex-1">
              <p className="text-sm font-medium">
                {customer.name} {customer.lastName}
              </p>
              {customer.email && customer.emailVisibility !== "hidden" && (
                <p className="text-xs">{customer.email}</p>
              )}
              {customer.gsm && customer.gsmVisibility !== "hidden" && (
                <p className="text-xs">{customer.gsm}</p>
              )}
            </div>
            {customer.notes && (
              <div
                style={{
                  flex: 1,
                }}
              >
                <Text variant={TextVariant.Caption} c={"gray"}>
                  {customer.notes.length > 50
                    ? `${customer.notes.substring(0, 50)}...`
                    : customer.notes}
                </Text>
              </div>
            )}
          </div>
          <Divider className="mt-2 w-full" />
        </div>
      ))}
    </div>
  );
});

export default memo(Form);
